import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function FilterPanel({
  variant = "drawer",
  value,
  onChange,
  enums,
  onClose,
  onClear,
}) {
  const set = (patch) => onChange((prev) => ({ ...prev, ...patch }));
  const toggle = (key, token) =>
    onChange((prev) => {
      const curr = new Set(prev[key] || []);
      curr.has(token) ? curr.delete(token) : curr.add(token);
      return { ...prev, [key]: [...curr] };
    });

  const Header = () => (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        bgcolor: "background.paper",
      }}>
      {/* Drawer handle on mobile */}
      {variant === "drawer" && (
        <Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: "text.disabled",
            }}
          />
        </Box>
      )}
      <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>
          Filters
        </Typography>
        <IconButton aria-label="Close filters" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: variant === "drawer" ? "85vh" : "80vh",
      }}>
      <Header />

      <Box sx={{ px: 2, py: 1, overflow: "auto" }}>
        {/* Singles */}
        <Section title="Shape" defaultExpanded>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={value.shape}
            onChange={(_, v) => set({ shape: v || "any" })}
            sx={{ flexWrap: "wrap" }}>
            <ToggleButton value="any">Any</ToggleButton>
            {enums.shapes.map((s) => (
              <ToggleButton key={s} value={s}>
                {labelize(s)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Section>

        <Section title="Buttercream finish" defaultExpanded>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={value.buttercreamFinish}
            onChange={(_, v) => set({ buttercreamFinish: v || "any" })}
            sx={{ flexWrap: "wrap" }}>
            <ToggleButton value="any">Any</ToggleButton>
            {enums.finishes.map((s) => (
              <ToggleButton key={s} value={s}>
                {labelize(s)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Section>

        {/* Multis */}
        <FacetChips
          title="Palette"
          tokens={enums.palette}
          active={value.palette}
          onToggle={(t) => toggle("palette", t)}
        />
        <FacetChips
          title="Themes"
          tokens={enums.themes}
          active={value.themes}
          onToggle={(t) => toggle("themes", t)}
        />
        <FacetChips
          title="Floral kinds"
          tokens={enums.floralKinds}
          active={value.floralKinds}
          onToggle={(t) => toggle("floralKinds", t)}
        />
        <FacetChips
          title="Floral colors"
          tokens={enums.floralColors}
          active={value.floralColors}
          onToggle={(t) => toggle("floralColors", t)}
        />
        <FacetChips
          title="Accents"
          tokens={enums.accents}
          active={value.accents}
          onToggle={(t) => toggle("accents", t)}
        />
      </Box>

      {/* Sticky actions */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          bgcolor: "background.paper",
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          p: 2,
        }}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button variant="text" onClick={onClear}>
            Clear all
          </Button>
          <Button variant="contained" onClick={onClose}>
            Apply
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function Section({ title, children, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{ "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

function FacetChips({ title, tokens = [], active = [], onToggle }) {
  return (
    <Section title={title}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0,1fr))",
            sm: "repeat(3, minmax(0,1fr))",
          },
          gap: 1,
        }}>
        {tokens.map((t) => {
          const selected = active.includes(t);
          return (
            <Chip
              key={t}
              label={labelize(t)}
              size="small"
              color={selected ? "primary" : "default"}
              onClick={() => onToggle(t)}
            />
          );
        })}
      </Box>
    </Section>
  );
}

function labelize(s = "") {
  return String(s)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}
